import { mapActions } from "vuex";
import dynamicsHead from "@/mixins/dynamics-head";
import { globalLoader, pluralizate } from "@/helpers/variables";
import CustomPagination from "@/components/custom-pagination/index.vue";
import AuthorHero from "./components/author-hero/index.vue";
import AuthorSection from "./components/author-section/index.vue";
import AuthorArticleCard from "./components/author-article-card/index.vue";
import PageNotFound from "@/modules/system/page-not-found/index.vue";

const MOBILE_BREAKPOINT = 768;
const TOPICS_AUTOPLAY_INTERVAL = 4000;
const TOPICS_SLIDE_DURATION = 900;

const easeInOutCubic = t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

export default {
  name: "author-page",
  mixins: [dynamicsHead],
  components: { CustomPagination, AuthorHero, AuthorSection, AuthorArticleCard, PageNotFound },
  data() {
    return {
      author: null,
      isNotFound: false,
      articles: [],
      page: 1,
      perPage: 6,
      total: 0,
      lastPage: 1,
      articlesLoading: false,
      activeTopic: 0,
      topicsTimer: null,
      topicsFrame: null,
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    isMobile() {
      return this.windowWidth <= MOBILE_BREAKPOINT;
    },
    hasMoreArticles() {
      return this.page < this.lastPage;
    },
    materialsLabel() {
      return pluralizate(this.total, "матеріал", "матеріали", "матеріалів");
    },
    sectionNumbers() {
      const author = this.author || {};
      const sections = {
        biography: !!(author.biographyLead || author.biography),
        topics: !!author.topics?.length,
        achievements: !!author.achievements?.length,
        articles: true,
      };
      let counter = 0;
      return Object.keys(sections).reduce((result, key) => {
        result[key] = sections[key] ? ++counter : 0;
        return result;
      }, {});
    },
  },
  watch: {
    page(value, oldValue) {
      if (this.isMobile && value > oldValue) {
        this.fetchArticles(true);
        return;
      }
      this.fetchArticles().then(this.scrollToArticles);
    },
  },
  async created() {
    globalLoader(true);
    try {
      this.author = await this.fetchAuthor(this.$route.params.slug);
    } catch (e) {
      globalLoader(false);
      this.isNotFound = true;
      this.addNoIndexNoFollow();
      return;
    }
    this.setMeta();
    await this.fetchArticles();
    globalLoader(false);
    this.$nextTick(() => {
      this.startTopicsAutoplay();
      if (this.$route.hash === "#articles") this.scrollToArticles();
    });
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    this.stopTopicsAutoplay();
    window.removeEventListener("resize", this.handleResize);
    this.removeJsonLd("person-jsonld");
  },
  methods: {
    ...mapActions({
      fetchAuthor: "pages/GET_AUTHOR",
      fetchAuthorArticles: "pages/GET_AUTHOR_ARTICLES",
    }),
    async fetchArticles(append = false) {
      this.articlesLoading = true;
      try {
        const response = await this.fetchAuthorArticles({
          slug: this.author.slug,
          page: this.page,
          perPage: this.perPage,
        });
        this.articles = append ? [...this.articles, ...response.data] : response.data;
        this.total = response.meta?.total || this.articles.length;
        this.lastPage = response.meta?.lastPage || 1;
      } catch (e) {
        console.error("ERROR FETCHING AUTHOR ARTICLES:", e);
      } finally {
        this.articlesLoading = false;
      }
    },
    loadMoreArticles() {
      this.page += 1;
    },
    handleResize() {
      const wasMobile = this.isMobile;
      this.windowWidth = window.innerWidth;
      if (wasMobile !== this.isMobile && this.page !== 1) {
        this.page = 1;
      }
    },
    scrollToArticles() {
      const element = this.$refs.articles;
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    startTopicsAutoplay() {
      this.stopTopicsAutoplay();
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      this.topicsTimer = setInterval(this.nextTopic, TOPICS_AUTOPLAY_INTERVAL);
    },
    stopTopicsAutoplay() {
      clearInterval(this.topicsTimer);
      this.topicsTimer = null;
      this.stopTopicsSlide();
    },
    stopTopicsSlide() {
      cancelAnimationFrame(this.topicsFrame);
      this.topicsFrame = null;
      if (this.$refs.topics) this.$refs.topics.style.scrollSnapType = "";
    },
    slideTopicsTo(left) {
      const container = this.$refs.topics;
      const from = container.scrollLeft;
      const to = Math.min(left, container.scrollWidth - container.clientWidth);
      let start = null;
      this.stopTopicsSlide();
      container.style.scrollSnapType = "none";
      const step = time => {
        if (start === null) start = time;
        const progress = Math.min((time - start) / TOPICS_SLIDE_DURATION, 1);
        container.scrollLeft = from + (to - from) * easeInOutCubic(progress);
        if (progress < 1) {
          this.topicsFrame = requestAnimationFrame(step);
        } else {
          this.stopTopicsSlide();
        }
      };
      this.topicsFrame = requestAnimationFrame(step);
    },
    nextTopic() {
      if (document.hidden) return;
      const isLast = this.activeTopic >= this.author.topics.length - 1;
      this.slideToTopic(isLast ? 0 : this.activeTopic + 1);
    },
    goToTopic(index) {
      this.stopTopicsAutoplay();
      this.slideToTopic(index);
    },
    slideToTopic(index) {
      const container = this.$refs.topics;
      const firstItem = container?.firstElementChild;
      if (!firstItem || container.scrollWidth <= container.clientWidth) return;
      const step = firstItem.offsetWidth + parseFloat(getComputedStyle(container).columnGap || 0);
      this.slideTopicsTo(index * step);
    },
    onTopicsScroll() {
      const container = this.$refs.topics;
      const firstItem = container?.firstElementChild;
      if (!firstItem) return;
      const isEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;
      const step = firstItem.offsetWidth + parseFloat(getComputedStyle(container).columnGap || 0);
      const lastIndex = this.author.topics.length - 1;
      this.activeTopic = isEnd ? lastIndex : Math.min(Math.round(container.scrollLeft / step), lastIndex);
    },
    formatNumber(value) {
      return String(value).padStart(2, "0");
    },
    authorUrl() {
      return `${window.location.origin}${this.$route.path}`;
    },
    setMeta() {
      const author = this.author;
      const title = author.metaTitle || `${author.name} — SkinOn`;
      const description = author.metaDescription || author.shortBio;

      this.changeTitle(title);
      this.changeMetaInfo("title", title);
      this.changeMetaInfo("og:title", title);
      this.changeMetaInfo("description", description);
      this.changeMetaInfo("og:description", description);
      this.addIndexFollow();
      this.setCanonical(this.authorUrl());

      this.setJsonLd("person-jsonld", {
        "@context": "https://schema.org",
        "@type": "Person",
        name: author.name,
        jobTitle: author.position,
        description: author.shortBio,
        image: new URL(author.image, window.location.origin).href,
        url: this.authorUrl(),
        worksFor: { "@type": "Organization", name: "SkinOn", url: window.location.origin },
        sameAs: (author.links || []).map(link => link.url),
      });
    },
  },
};
